import React, { useEffect, useMemo, useRef, useState } from "react";
import { css } from "goober";

import { classNames } from "@/util/class-names.mjs";
import nextFrame from "@/util/next-frame.mjs";
import { nullishMerge } from "@/util/optional-merge.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";

// Animation which uses ref fn for show, and removal for hide,
// While leaving the element is inert
export function useInertTransition<E extends HTMLElement = HTMLElement>({
  timeout = null,
  onEnter,
  onLeave,
  anim: animProp = ANIM,
}: {
  timeout?: number | null;
  onEnter?: (element: E) => void;
  onLeave?: (element: E) => void;
  anim?: Anim;
} = {}): React.RefObject<E> & ((node: E, skipEnter?: boolean) => void) {
  const anim = useMemo<Anim>(() => processAnim(animProp), [animProp]);

  const enterNode = useCallbackRef((node: E, skipEnter?: boolean) => {
    applySizeCssVars(node);

    if ((skipEnter || !enterNode["rendered"]) && !node.dataset.animInit) {
      enterNode["rendered"] = true;
      base.enter(node, anim);
      node.classList.add(cssNoAnimate());
      animate.enter(node, anim);
    } else {
      base.enter(node, anim);
      node.classList.add(cssNoAnimate());
      onEnter?.(node);
      nextFrame(() => {
        animate.enter(node, anim);
      });
    }

    // setup removal obs
    const parent = node.parentElement;
    if (!parent) return;
    const obs = new MutationObserver((mutations) => {
      for (const { removedNodes } of mutations) {
        removedNodes.forEach((el) => {
          if (el !== node) return;
          node.inert = true;
          parent.appendChild(node);

          base.leave(node, anim);
          onLeave?.(node);
          nextFrame(() => {
            animate.leave(node, anim);
            setTimeout(
              () => {
                node.remove();
              },
              timeout ?? getTransitionDuration(node),
            );
          });
          obs.disconnect();
        });
      }
    });

    obs.observe(parent, { childList: true });

    return () => {
      setTimeout(() => obs.disconnect());
    };
  }, []);

  return enterNode;
}

// has effects of useInertTransition on all child elements
export const useInertTransitionParent = (
  options: Arguments<typeof useInertTransition>[0] = {},
) => {
  const anim = useMemo<Anim>(() => processAnim(options.anim), [options.anim]);
  const enterNode = useInertTransition(options);

  const parentCallback = useCallbackRef((parent: HTMLElement) => {
    if (!(parent instanceof HTMLElement)) return;
    for (const child of parent.children) enterNode(child as HTMLElement, true); // skips initial animation on mount

    const obs = new MutationObserver((mutations) => {
      for (const { addedNodes } of mutations) {
        addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.classList.contains(anim.leave)) return;
          enterNode(node);
        });
      }
    });
    obs.observe(parent, { childList: true });
    return () => obs.disconnect();
  }, []);

  return parentCallback;
};

// Simple Transition component using DOM animation frames.
// This is a direct replacement for the Transition component from @headlessui/react.
const Transition = (
  {
    as = "div",
    show,
    anim: animProp = ANIM,
    className = "",
    timeout = null,
    onShow,
    onHide,
    ...props
  }: TransitionProps,
  forwardedRef: React.MutableRefObject<HTMLElement>,
) => {
  const anim = useMemo<Anim>(() => processAnim(animProp), [animProp]);
  const [shouldRender, setShouldRender] = useState(show);
  // we do not want to animate in or out in the initial show
  const {
    current: { firstRender },
    current,
  } = useRef({ firstRender: false });

  const ref = useCallbackRef(
    (node: HTMLElement) => {
      if (current.firstRender) {
        animate.enter(node, anim);
        node.classList.add(cssNoAnimate());
        current.firstRender = false;
        return;
      }

      base.enter(node, anim);
      const next = nextFrame(() => {
        animate.enter(node, anim);
      });

      onShow?.(node);
      return () => {
        cancelAnimationFrame(next.frame);
      };
    },
    [],
    forwardedRef,
  );

  useEffect(() => {
    if (!ref.current) return setShouldRender(show);
    if (show) {
      if (firstRender) return; // setFirstRender is done in callbackRef!
      base.enter(ref.current, anim);
      const next = nextFrame(() => animate.enter(ref.current, anim));
      return () => cancelAnimationFrame(next.frame);
    }
    if (current.firstRender) {
      current.firstRender = false;
      return;
    }

    // on hide we apply the animation class then unrender after the animation is complete
    base.leave(ref.current, anim);
    onHide?.(ref.current);
    let t;
    const next = nextFrame(() => {
      animate.leave(ref.current, anim);
      t = setTimeout(
        () => {
          setShouldRender(false);
        },
        timeout ?? getTransitionDuration(ref.current),
      );
    });
    return () => {
      cancelAnimationFrame(next.frame);
      clearTimeout(t);
    };
  }, [show, anim, current, firstRender, onHide, ref, timeout]);

  const Elem = as as unknown as () => JSX.Element;

  return (
    shouldRender && <Elem ref={ref} {...classNames(className)} {...props} />
  );
};

const cssNoAnimate = () => css`
  & {
    transition: none !important;
  }
`;

const cssNoInteract = () => css`
  & {
    pointer-events: none !important;
  }
  * {
    pointer-events: none !important;
  }
`;

const applyClasses = (
  element: HTMLElement,
  ...actions: ["add" | "remove", (string | ((el: HTMLElement) => string))[]][]
) => {
  if (!element) return;
  actions.forEach(([method, classes]) => {
    const filteredClasses = classes
      .filter(Boolean)
      .map((c) => (typeof c === "function" ? c(element) : c));
    if (filteredClasses.length) {
      element.classList[method](...filteredClasses);
    }
  });
};

const base: UtilFns = {
  enter(element, anim) {
    applyClasses(
      element,
      ["remove", [cssNoAnimate(), anim.leave, anim.leaveFrom, anim.leaveTo]],
      ["add", [anim.base, anim.enter, anim.enterFrom]],
    );
  },
  leave(element, anim) {
    applyClasses(
      element,
      ["remove", [cssNoAnimate(), anim.enter, anim.enterFrom, anim.enterTo]],
      ["add", [cssNoInteract(), anim.base, anim.leave, anim.leaveFrom]],
    );
  },
};
const animate: UtilFns = {
  enter(element, anim) {
    applyClasses(
      element,
      ["remove", [anim.enterFrom, cssNoAnimate()]],
      ["add", [anim.enterTo]],
    );
  },
  leave(element, anim) {
    applyClasses(
      element,
      ["remove", [anim.leaveFrom, cssNoAnimate()]],
      ["add", [anim.leaveTo]],
    );
  },
};

export const getTransitionDuration = (element: HTMLElement) => {
  if (!element) return;
  const duration = getComputedStyle(element)
    .transitionDuration?.split(",")
    .reduce((acc, cur) => {
      return Math.max(acc, parseFloat(cur) * (cur.endsWith("s") ? 1000 : 1));
    }, 0);
  return duration;
};

type Anim = PartialRecord<keyof typeof ANIM, string>;

// optional animation object for convenience
const ANIM = {
  base: "transition",
  enter: "enter",
  enterFrom: "enter-from",
  enterTo: "enter-to",
  leave: "leave",
  leaveFrom: "leave-from",
  leaveTo: "leave-to",
};
export const ANIMATION_CLASSES = ANIM;

const processAnim = (anim = ANIM as Anim): Anim =>
  nullishMerge(
    nullishMerge(anim, {
      enterFrom: anim.leaveTo,
      enterTo: anim.leaveFrom,
      leaveFrom: anim.enterTo,
      leaveTo: anim.enterFrom,
    }),
    ANIM,
  );

type TransitionProps = {
  as?: keyof JSX.IntrinsicElements;
  show: boolean;
  anim?: Anim;
  nFrame?: number;
  // ms to wait before unrendering
  // can omit to automatically detect based on the root's transition property
  timeout?: number;
  skipFirstRender?: boolean;
  ref?: React.MutableRefObject<HTMLElement>;
  onShow?: (node: HTMLElement) => void;
  onHide?: (node: HTMLElement) => void;
} & React.HTMLAttributes<HTMLElement>;

type UtilFns = Record<"enter" | "leave", (e: HTMLElement, anim: Anim) => void>;

const from = (css) => `&.enter-from, &.leave-to {${css}}`;
const leaveAbsolute = `&.leave { position: absolute; top: 0; max-height: 100vh; }`;

export const TRANSITION = {
  fade: () => css`
    transition: opacity var(--transition);
    ${from("opacity: 0;")}
    ${leaveAbsolute}
  `,
  slide: (distance = "10px") => css`
    transition: transform var(--transition);
    ${from(`transform: translateX(${distance})`)}
    ${leaveAbsolute}
  `,
};

function toKebabCase(str: string) {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

function applySizeCssVars(node) {
  const sizes = [
    "offsetLeft",
    "offsetTop",
    "offsetWidth",
    "offsetHeight",
  ] as const;

  for (const dim of sizes)
    node.style.setProperty(`--${toKebabCase(dim)}`, `${node[dim]}px`);
}

export default React.forwardRef(Transition);
